
(async function () {
  const tag = 'np-gift-npcart';
  if (customElements.get(tag)) return;
  const { CartLinesUpdateEvent, StandardEvents } = await import('@shopify/events');
  const { DrawerOpenEvent } = await import('@theme/theme-drawer');
  const shared = window.__npGiftCoordinatorV2 ||= { pending: null, stateRequest: null };
  function signature(cart) {
    return JSON.stringify([cart.currency, cart.total_price, cart.items.map(l => [l.key, l.quantity, l.final_line_price])]);
  }
  function summarize(cart, memberships, collectionId, giftId) {
    let count = 0;
    const gifts = [];
    for (const line of cart.items) {
      const marked = String(line.properties?._np_free_gift || '') === '1';
      if (String(line.product_id) === giftId) {
        if (marked) gifts.push(line);
        continue;
      }
      if (!marked && (memberships[String(line.product_id)] || []).map(String).includes(collectionId)) {
        count += Number(line.quantity) || 0;
      }
    }
    const quantity = gifts.reduce((total, line) => total + Number(line.quantity), 0);
    const free = quantity === 1 && gifts.every(line => typeof line.final_line_price === 'number' && line.final_line_price === 0);
    return { count, gifts, quantity, free };
  }
  class NpGift extends HTMLElement {
    connectedCallback() {
      if (this.connected) return;
      this.connected = true;
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.ui = Object.fromEntries(['title', 'copy', 'fill', 'progress', 'gift', 'variant', 'add', 'status'].map(name => [name, this.querySelector('[data-' + name + ']')]));
      this.bar = this.querySelector('[role="progressbar"]');
      this.giftImage = this.querySelector('[data-gift] img');
      try { this.giftImages = JSON.parse(this.querySelector('[data-gift-images]')?.textContent || '{}'); } catch { this.giftImages = {}; }
      this.defaultGiftImage = this.giftImage?.getAttribute('src') || '';
      this.defaultGiftAlt = this.giftImage?.getAttribute('alt') || '';
      this.imageVersion = 0;
      this.colorUnlocked = false;
      this.colorControls = this.querySelector('[data-color-controls]');
      this.colorHint = this.querySelector('[data-color-hint]');
      this.root = this.dataset.root || '/';
      this.collectionId = this.dataset.collectionId;
      this.giftId = this.dataset.giftId;
      this.configured = Boolean(this.collectionId && this.giftId);
      this.busy = false;
      this.lastSignature = null;
      Array.from(this.ui.variant.options).filter(option => option.disabled).forEach(option => option.remove());
      const first = Array.from(this.ui.variant.options).find(option => !option.disabled);
      if (first) this.ui.variant.value = first.value;
      this.ui.add.disabled = true;
      this.querySelector('[data-swatches]')?.addEventListener('change', event => {
        const radio = event.target.closest('[data-swatch-radio]');
        if (!radio || !radio.checked || !this.colorUnlocked) return;
        const option = Array.from(this.ui.variant.options).find(item => item.value === radio.value && !item.disabled);
        if (!option) return;
        this.ui.variant.value = option.value;
        this.ui.variant.dispatchEvent(new Event('change', { bubbles: true }));
      }, { signal });
      this.ui.add.addEventListener('click', () => this.claim(), { signal });
      this.ui.variant.addEventListener('change', () => { this.updateGiftImage(); if (this.state) this.render(this.state); }, { signal });
      this.updateGiftImage();
      this.ui.retry = document.createElement('button');
      this.ui.retry.type = 'button'; this.ui.retry.textContent = 'Retry'; this.ui.retry.hidden = true;
      this.ui.retry.addEventListener('click', () => this.refresh(true), { signal });
      this.ui.status.after(this.ui.retry);
      const onSettled = event => {
        if (event.promise) event.promise.then(() => this.refresh(true), () => this.refresh(true));
        else this.refresh(true);
      };
      document.addEventListener(StandardEvents.cartLinesUpdate, onSettled, { signal });
      document.addEventListener(StandardEvents.cartDiscountUpdate, onSettled, { signal });
      document.addEventListener(DrawerOpenEvent.eventName, () => this.refresh(true), { capture: true, signal });
      document.addEventListener('visibilitychange', () => { if (!document.hidden) this.refresh(true); }, { signal });
      window.addEventListener('pageshow', () => this.refresh(true), { signal });
      // Fallback for third-party AJAX mutations that do not emit standard events. No global fetch patching.
      this.timer = setInterval(() => { if (!document.hidden) this.refresh(); }, 5000);
      this.refresh(true);
      shared.pending?.finally(() => { if (this.connected && this.state) this.render(this.state); }).catch(() => {});
    }
    disconnectedCallback() {
      this.connected = false;
      this.abort?.abort(); clearInterval(this.timer); this.ui?.retry?.remove();
    }
    syncSwatches() {
      const available = new Set(Array.from(this.ui.variant.options).filter(option => !option.disabled).map(option => option.value));
      this.querySelectorAll('[data-swatch-radio]').forEach(radio => {
        const enabled = available.has(radio.value);
        radio.disabled = !enabled;
        radio.closest('[data-swatch-item]').hidden = !enabled;
        radio.checked = enabled && radio.value === this.ui.variant.value;
      });
    }
    updateGiftImage() {
      if (!this.giftImage) return;
      const chosen = this.colorUnlocked ? this.giftImages[this.ui.variant.value] : null;
      const src = chosen?.src || this.defaultGiftImage;
      const alt = chosen?.alt || this.defaultGiftAlt;
      if (!src) return;
      this.giftImage.alt = alt;
      if (this.giftImage.getAttribute('src') === src) return;
      const version = ++this.imageVersion;
      this.giftImage.removeAttribute('srcset');
      this.giftImage.removeAttribute('sizes');
      this.giftImage.onerror = () => {
        if (version !== this.imageVersion) return;
        this.giftImage.onerror = null;
        if (this.defaultGiftImage && this.giftImage.getAttribute('src') !== this.defaultGiftImage) {
          this.giftImage.src = this.defaultGiftImage;
          this.giftImage.alt = this.defaultGiftAlt;
        }
      };
      this.giftImage.src = src;
    }
    endpoint(path) { return this.root.replace(/\/$/, '') + '/' + path; }
    async cart() {
      const response = await fetch(this.endpoint('cart.js'), { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } });
      if (!response.ok) throw Error('Could not read cart (' + response.status + ').');
      const cart = await response.json();
      if (!Array.isArray(cart.items)) throw Error('Invalid cart response.');
      return cart;
    }
    async serverState() {
      if (!shared.stateRequest) {
        shared.stateRequest = (async () => {
          const url = new URL(this.root, location.origin);
          url.searchParams.set('section_id', 'np-gift-cart-state');
          url.searchParams.set('_np', String(Date.now()));
          const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store' });
          if (!response.ok) throw Error('Could not refresh gift eligibility (' + response.status + ').');
          const html = await response.text();
          const node = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-np-gift-cart-state]');
          if (!node) throw Error('Install sections/np-gift-cart-state.liquid before testing.');
          const state = JSON.parse(node.textContent);
          if (!Array.isArray(state.cart?.items) || !state.memberships) throw Error('Invalid eligibility response.');
          return state;
        })().finally(() => { shared.stateRequest = null; });
      }
      return shared.stateRequest;
    }
    async verifiedState() {
      // Retry once if another component changes the cart during the two reads.
      for (let attempt = 0; attempt < 2; attempt++) {
        const state = await this.serverState();
        const latest = await this.cart();
        if (signature(state.cart) === signature(latest)) return state;
      }
      throw Error('Your cart changed while checking the gift. Please retry.');
    }
    async refresh(force = false) {
      if (!this.connected || this.busy) return;
      if (this.refreshing) { this.refreshQueued = this.refreshQueued || force; return; }
      if (!this.configured) {
        this.fail('Choose Eligible collection and Gift product, then save the theme settings.'); return;
      }
      this.refreshing = true;
      try {
        const cart = await this.cart();
        if (force || signature(cart) !== this.lastSignature) {
          const state = await this.verifiedState();
          if (!this.connected) return;
          this.state = state; this.lastSignature = signature(state.cart); this.render(state);
        }
      } catch (error) { if (this.connected) this.fail(error.message); }
      finally {
        this.refreshing = false;
        if (this.refreshQueued && this.connected) { this.refreshQueued = false; queueMicrotask(() => this.refresh(true)); }
      }
    }
    fail(message) {
      this.state = null; this.lastSignature = null;
      this.ui.add.disabled = true; this.ui.gift.hidden = false;
      this.colorUnlocked = false; this.colorControls.hidden = true; this.colorHint.hidden = false; this.updateGiftImage();
      this.ui.progress.textContent = 'Progress unavailable';
      this.ui.fill.style.width = '0%'; this.bar.setAttribute('aria-valuenow', '0');
      this.ui.status.classList.remove('ai-gift-npcart__status--good');
      this.ui.status.textContent = message; this.ui.retry.hidden = false;
      document.querySelectorAll('[data-np-header-progress]').forEach(header => {
        header.querySelector('[data-np-header-count]').textContent = 'Progress unavailable';
        header.querySelector('[data-np-header-copy]').textContent = 'Gift eligibility could not be verified. Please retry.';
        header.querySelector('[data-np-header-title]').textContent = 'BUY 2, GET A FREE HAT';
        header.querySelector('[data-np-header-fill]').style.width = '0%';
        header.querySelector('[role="progressbar"]').setAttribute('aria-valuenow', '0');
      });
    }
    render(state) {
      const result = summarize(state.cart, state.memberships, this.collectionId, this.giftId);
      const count = Math.min(result.count, 2);
      this.ui.progress.textContent = count + '/2';
      this.ui.fill.style.width = count * 50 + '%';
      this.ui.fill.style.background = count === 2 ? '#176b3a' : '#111';
      this.bar.setAttribute('aria-valuenow', String(count));
      this.ui.title.textContent = count === 0 ? 'BUY 2, GET A FREE HAT' : count === 1 ? 'ADD 1 MORE TO GET A FREE HAT!' : "YOU'VE UNLOCKED A FREE HAT!";
      this.ui.copy.textContent = count === 0 ? 'Add any 2 eligible items to unlock your free gift.' : count === 1 ? "You're just one item away from your free gift." : 'Your order qualifies for 1 free hat.';

      document.querySelectorAll('[data-np-header-progress]').forEach(header => {
        header.querySelector('[data-np-header-title]').textContent = this.ui.title.textContent;
        header.querySelector('[data-np-header-copy]').textContent = this.ui.copy.textContent;
        header.querySelector('[data-np-header-count]').textContent = count + '/2';
        header.querySelector('[data-np-header-fill]').style.width = count * 50 + '%';
        header.querySelector('[data-np-header-fill]').style.background = count === 2 ? '#176b3a' : '#111';
        header.querySelector('[role="progressbar"]').setAttribute('aria-valuenow', String(count));
      });
      this.ui.gift.hidden = false;
      this.ui.retry.hidden = true;
      this.ui.status.classList.toggle('ai-gift-npcart__status--good', count === 2 && result.free);
      Array.from(this.ui.variant.options).filter(option => option.disabled).forEach(option => option.remove());
      const options = Array.from(this.ui.variant.options);
      const first = options.find(option => !option.disabled);
      if ((!this.ui.variant.selectedOptions[0] || this.ui.variant.selectedOptions[0].disabled) && first) this.ui.variant.value = first.value;
      this.colorUnlocked = count === 2;
      this.colorControls.hidden = !this.colorUnlocked || !first;
      this.colorHint.hidden = this.colorUnlocked;
      this.updateGiftImage();
      this.syncSwatches();
      this.dataset.giftUnlocked = String(this.colorUnlocked);
      this.ui.add.hidden = result.quantity > 0 || count < 2;
      this.ui.add.textContent = count < 2 ? 'ADD ' + (2 - count) + ' MORE TO UNLOCK' : 'ADD FREE HAT';
      this.ui.add.disabled = this.busy || Boolean(shared.pending) || count < 2 || result.quantity > 0 || !first || !this.ui.variant.value || this.ui.variant.selectedOptions[0]?.disabled;
      this.ui.status.textContent = '';
      if (result.quantity > 1) this.ui.status.textContent = 'More than one promotional hat is in your cart. Only one free hat is allowed; review the gift quantities in your cart.';
      else if (result.quantity && count < 2) this.ui.status.textContent = 'Your free hat is no longer eligible. It remains in your cart and may be charged. Remove the promotional hat or add eligible items before checkout.';
      else if (result.quantity && result.free) this.ui.status.textContent = 'FREE HAT ADDED SUCCESSFULLY!';
      else if (result.quantity) this.ui.status.textContent = 'Gift added, but the free discount is not confirmed. Review your cart before checkout.';
      else if (!first) this.ui.status.textContent = 'Sorry, this free gift is currently out of stock.';
      const existingPrice = this.ui.gift.querySelector('[data-gift-price]');
      if (existingPrice) existingPrice.textContent = result.free && count === 2 ? 'FREE · ' + new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: state.cart.currency }).format(0) : 'FREE';
    }
    async claim() {
      if (this.busy || shared.pending || !this.state || this.ui.add.disabled) return;
      const task = async () => {
        this.busy = true; this.ui.add.disabled = true;
        let deferred, wasAdded = false;
        try {
          const state = await this.verifiedState();
          const result = summarize(state.cart, state.memberships, this.collectionId, this.giftId);
          if (result.count < 2 || result.quantity) { this.state = state; this.render(state); return; }
          const selected = this.ui.variant.selectedOptions[0];
          if (!selected || selected.disabled) throw Error('Choose an available gift color.');
          // Refresh server-rendered stock for this block before submitting. Do not trust old page stock.
          const stockUrl = new URL(this.dataset.stockUrl, location.origin);
          stockUrl.searchParams.set('section_id', this.dataset.sectionId);
          stockUrl.searchParams.set('_np', String(Date.now()));
          const stockResponse = await fetch(stockUrl, { credentials: 'same-origin', cache: 'no-store' });
          if (!stockResponse.ok) throw Error('Could not verify gift stock.');
          const doc = new DOMParser().parseFromString(await stockResponse.text(), 'text/html');
          const freshBlock = Array.from(doc.querySelectorAll('[data-np-gift-block]')).find(node => node.dataset.blockId === this.dataset.blockId);
          if (!freshBlock || freshBlock.dataset.giftId !== this.giftId || freshBlock.dataset.collectionId !== this.collectionId) throw Error('Save the selected gift and collection in the theme editor before claiming.');
          const freshOption = Array.from(freshBlock.querySelectorAll('[data-variant] option')).find(option => option.value === selected.value);
          if (!freshOption || freshOption.disabled) { selected.disabled = true; throw Error('This gift color is sold out. Please choose another.'); }
          const variantId = selected.value;
          const sectionIds = Array.from(new Set(Array.from(document.querySelectorAll('cart-items-component')).map(node => node.dataset.sectionId).filter(Boolean))).slice(0, 5);
          deferred = CartLinesUpdateEvent.createPromise();
          this.dispatchEvent(new CartLinesUpdateEvent({ action: 'add', context: 'product', lines: [{ merchandiseId: variantId, quantity: 1 }], promise: deferred.promise }));
          const response = await fetch(this.endpoint('cart/add.js'), { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1, properties: { _np_free_gift: '1' } }], sections: sectionIds, sections_url: location.pathname }) });
          const added = await response.json();
          if (!response.ok) throw Error(added.description || 'The gift could not be added.');
          wasAdded = true;
          const updated = await this.cart();
          deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(updated), detail: { source: 'np-gift-promotion', sourceId: this.dataset.blockId, items: updated.items, itemCount: updated.item_count, sections: added.sections, didError: false } });
          deferred = null;
          this.state = await this.verifiedState(); this.lastSignature = signature(this.state.cart); this.render(this.state);
        } catch (error) {
          if (deferred) {
            try { const cart = await this.cart(); deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { source: 'np-gift-promotion', itemCount: cart.item_count, didError: true } }); }
            catch (readError) { deferred.reject(readError); }
          }
          this.fail((wasAdded ? 'The hat was added, but verification failed. Check your cart before retrying. ' : '') + error.message);
        } finally { this.busy = false; }
      };
      // Web Locks serializes gift claims across same-origin tabs when available.
      shared.pending = navigator.locks ? navigator.locks.request('niceprimes-free-hat-claim', task) : task();
      try { await shared.pending; } finally { shared.pending = null; if (this.state) this.render(this.state); }
    }
  }
  if (!customElements.get(tag)) customElements.define(tag, NpGift);
})().catch(error => {
  console.error('[NICEPRIMES gift]', error);
  document.querySelectorAll('np-gift-npcart [data-status]').forEach(node => { node.textContent = 'Gift module could not initialize. Please reload or contact support.'; });
});

// Header-only observer: no independent gift card, hidden widget, or gift add call.
(async () => {
  if (window.__npGiftHeaderObserver) return;
  window.__npGiftHeaderObserver = true;
  const { StandardEvents } = await import('@shopify/events');
  const { DrawerOpenEvent } = await import('@theme/theme-drawer');
  const coordinator = window.__npGiftCoordinatorV2 ||= { pending:null, stateRequest:null };
  function summarize(cart, memberships, collectionId, giftId) {
    let count = 0;
    const gifts = [];
    for (const line of cart.items) {
      const marked = String(line.properties?._np_free_gift || '') === '1';
      if (String(line.product_id) === giftId) {
        if (marked) gifts.push(line);
        continue;
      }
      if (!marked && (memberships[String(line.product_id)] || []).map(String).includes(collectionId)) {
        count += Number(line.quantity) || 0;
      }
    }
    const quantity = gifts.reduce((total, line) => total + Number(line.quantity), 0);
    const free = quantity === 1 && gifts.every(line => typeof line.final_line_price === 'number' && line.final_line_price === 0);
    return { count, gifts, quantity, free };
  }
  let reading = false, queued = false;
  async function refreshHeader() {
    const first = document.querySelector('[data-np-header-progress]');
    if (!first || document.hidden) return;
    if (coordinator.pending) { coordinator.pending.finally(() => refreshHeader()).catch(() => {}); return; }
    if (reading) { queued = true; return; }
    reading = true;
    try {
      const url = new URL(first.dataset.root || '/', location.origin);
      url.searchParams.set('section_id','np-gift-cart-state');
      url.searchParams.set('_np',String(Date.now()));
      const response = await fetch(url,{credentials:'same-origin',cache:'no-store'});
      if (!response.ok) throw Error('Cart read failed');
      const doc = new DOMParser().parseFromString(await response.text(),'text/html');
      const node = doc.querySelector('[data-np-gift-cart-state]');
      if (!node) throw Error('Gift state missing');
      const state = JSON.parse(node.textContent);
      if (!Array.isArray(state.cart?.items) || !state.memberships) throw Error('Invalid gift state');
      document.querySelectorAll('[data-np-header-progress]').forEach(header => {
        const result = summarize(state.cart,state.memberships,header.dataset.collectionId,header.dataset.giftId);
        const count = Math.min(2,result.count);
        const label = count === 2 ? header.dataset.done : count === 1 ? header.dataset.one : header.dataset.zero;
        header.querySelector('[data-np-header-title]').textContent = String(label).replace(/&(?:amp;)?(?:#(?:0*39|x0*27)|apos);/gi, "'");
        header.querySelector('[data-np-header-copy]').textContent = result.quantity && (count < 2 || result.quantity > 1 || !result.free) ? header.dataset.warning : '';
        header.querySelector('[data-np-header-count]').textContent = count+'/2';
        const fill = header.querySelector('[data-np-header-fill]');
        fill.style.width = (count*50)+'%';
        fill.style.background = count === 2 ? '#176b3a' : count === 1 ? '#c49a2c' : '#dedede';
        header.querySelector('[role="progressbar"]').setAttribute('aria-valuenow',String(count));
      });
    } catch {
      document.querySelectorAll('[data-np-header-progress]').forEach(header => {
        header.querySelector('[data-np-header-title]').textContent = header.dataset.unknown;
        header.querySelector('[data-np-header-copy]').textContent = '';
        header.querySelector('[data-np-header-fill]').style.width = '0%';
        header.querySelector('[role="progressbar"]').setAttribute('aria-valuenow','0');
        header.querySelector('[data-np-header-count]').textContent = '—/2';
      });
    } finally { reading = false; if (queued) { queued = false; queueMicrotask(refreshHeader); } }
  }
  const settled = event => { if (event.promise) event.promise.then(refreshHeader,refreshHeader); else refreshHeader(); };
  document.addEventListener(StandardEvents.cartLinesUpdate,settled);
  document.addEventListener(StandardEvents.cartDiscountUpdate,settled);
  document.addEventListener(DrawerOpenEvent.eventName,refreshHeader,{capture:true});
  document.addEventListener('visibilitychange',() => { if (!document.hidden) refreshHeader(); });
  window.addEventListener('pageshow',refreshHeader);
  setInterval(() => { if (!document.hidden) refreshHeader(); },5000);
  refreshHeader();
})().catch(error => console.warn('[NICEPRIMES gift header]',error));
